import type { SyntheticEvent } from 'react';
import { GitMerge, GitPullRequest, RefreshCw } from 'lucide-react';
import { prMergeView, type GithubPrLive, type PrChecksSummary, type PrMergeView, type PrMigrationsBlock, type SharedPrSubtask } from '@shared/github-pr';
import { noPicturesHint } from '@shared/pr-pictures';
import { sharedPrParent, type TaskParent, type TaskPr, type TaskWaiting } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { statusToneClasses, type StatusMeta } from '@/components/status-tone';
import { ExplainedValue } from '@/components/ExplainedValue';
import { ExternalLink } from '@/components/ExternalLink';
import { StatusBadge } from '@/components/StatusBadge';
import { ReviewBadge } from '@/components/factory/ReviewBadge';
import { usePermission } from '@/hooks/usePermission';
import { useFormatTaskRef } from '@/lib/current-project';
import { PR_MERGE_VIEWS, TASK_PR_STATUSES, prLabel } from './task-types';
import { isOpenPr, prMergeState, prUpdateState, updateHint } from './task-pr-actions';
import { usePrMerge, usePrUpdate, useTaskPrsLive } from './use-task-prs';
import { mergeRunNote } from './merge-run-warning';
import { RunNoteIcon } from './TaskPrActions';
import { useTaskActiveRun } from '@/components/factory/active-runs';

/** SF-T-164: the badge on an open PR that changes what people see with no pictures in its description. */
const NO_PICTURES_META: StatusMeta = { label: 'No screenshots', tone: 'warning' };

/** What each state means, for the badge's hint. */
const VIEW_HINTS: Record<PrMergeView, string> = {
  ready: 'No conflicts and nothing blocking: it can be merged once its checks pass',
  conflicts: "It conflicts with its base branch and can't be merged until they're resolved",
  blocked: 'GitHub is blocking the merge: required reviews or checks',
  behind: 'The branch must be brought up to date with its base first',
  checking: 'GitHub is still working out whether it can merge',
  draft: "It's a draft",
  merged: "It's merged",
  closed: 'Closed without merging',
};

/**
 * One pull request on a card: its number (a link to GitHub), its state, its second-model review (T-36),
 * and Merge when it's ready and you may (off, with the reason, while its checks haven't passed or, on a
 * parent, its sub-tasks sharing it aren't ready). Without a live state (GitHub not connected, or the read
 * failed) it shows the stored status. SF-T-154: on a sub-task, a PR shared with its parent (`sharedWith`,
 * the parent's ref) shows its number and state and says so; its review and Merge are the parent's.
 * SF-T-164: an open PR the hub marked as changing what people see with no pictures says "No screenshots".
 * SF-T-183: a PR adding migrations while main has a newer one (`migrations`) shows Merge off saying so, and
 * Update beside it (`onUpdate`, for someone who may start runs).
 */
export function TaskPrRow({
  pr,
  live,
  checks,
  error,
  canMerge = false,
  onMerge,
  runNote = null,
  notReady,
  sharedWith,
  migrations,
  onUpdate,
  updateEnabled = true,
  updateReason = null,
  updating = false,
}: {
  pr: TaskPr;
  live: GithubPrLive | null;
  /** SF-T-79: the checks on a ready PR's head (null: not read, or couldn't be). */
  checks?: PrChecksSummary | null;
  /** Why there's no live state, as a hint on the stored status. */
  error?: string | null;
  /** Show Merge (the caller checked the permission); it's on only when GitHub says it's ready and the checks passed. */
  canMerge?: boolean;
  onMerge?: () => void;
  /** SF-T-237: a run still going on the task (mergeRunNote): Merge shows a warning icon and says so on hover. */
  runNote?: { tone: 'warning' | 'info'; text: string } | null;
  /** SF-T-154: on a parent, its sub-tasks sharing the PR that aren't ready (Merge stays off). */
  notReady?: SharedPrSubtask[] | null;
  /** SF-T-154: on a sub-task, the ref of the parent it shares the PR with. */
  sharedWith?: string | null;
  /** SF-T-183: main has a migration the PR's branch doesn't, or two migration heads (Merge stays off). */
  migrations?: PrMigrationsBlock | null;
  /** SF-T-183: show Update (the caller checked the permission and that it's behind). */
  onUpdate?: () => void;
  updateEnabled?: boolean;
  updateReason?: string | null;
  updating?: boolean;
}) {
  const view = live ? prMergeView(live) : null;
  const meta = view ? PR_MERGE_VIEWS[view] : TASK_PR_STATUSES[pr.status];
  const hint = view ? VIEW_HINTS[view] : (error ?? 'As last recorded on the task');
  const merge = prMergeState(live, checks, notReady, migrations);
  // SF-T-164: open, it changes files people see and its description has no screenshots (the hub's mark).
  const showNoPictures = Boolean(pr.no_pictures) && !sharedWith && isOpenPr(pr) && view !== 'merged' && view !== 'closed';
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
      <GitPullRequest className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <ExternalLink href={pr.url} size="xs" data-hint={live?.title ?? pr.url} className="shrink-0">
        {prLabel(pr.url)}
      </ExternalLink>
      <StatusBadge size="sm" meta={meta} data-hint={hint} />
      {sharedWith ? (
        <ExplainedValue className="shrink-0 text-muted-foreground" explanation={`One pull request for ${sharedWith}'s sub-tasks: it's reviewed and merged on ${sharedWith}.`}>
          Shared with {sharedWith}
        </ExplainedValue>
      ) : null}
      {pr.review && !sharedWith ? <ReviewBadge review={pr.review} /> : null}
      {showNoPictures ? <StatusBadge size="sm" meta={NO_PICTURES_META} data-hint={noPicturesHint(pr.no_pictures!)} /> : null}
      {onUpdate && live && !sharedWith ? (
        <Button
          type="button"
          size="xs"
          variant="outline"
          layoutClassName="ml-auto"
          disabled={!updateEnabled}
          disabledReason={updateReason}
          loading={updating}
          data-hint={updateHint(prLabel(pr.url), live.base_ref)}
          onClick={onUpdate}
        >
          <RefreshCw />
          Update
        </Button>
      ) : null}
      {canMerge && !sharedWith && merge.shown ? (
        <Button
          type="button"
          size="xs"
          variant="outline"
          layoutClassName={onUpdate && live ? undefined : 'ml-auto'}
          disabled={!merge.enabled}
          disabledReason={merge.reason}
          data-hint={runNote ? `${runNote.text}.` : undefined}
          onClick={onMerge}
        >
          {runNote ? <RunNoteIcon tone={runNote.tone} /> : <GitMerge />}
          Merge
        </Button>
      ) : null}
    </div>
  );
}

/** Stops a press inside the PR rows (or their dialog) from opening, picking or dragging the card. */
const stop = (e: SyntheticEvent) => e.stopPropagation();
const STOP_PROPS = {
  onClick: stop,
  onMouseDown: stop,
  onPointerDown: stop,
  onTouchStart: stop,
  onKeyDown: stop,
};

/**
 * SF-T-151: with more than one pull request, how many are merged ("2/3 merged"), the merged and closed
 * ones named in the hint; null with one, which shows as its row.
 */
export function TaskPrSummary({ prs }: { prs: TaskPr[] }) {
  if (prs.length < 2) return null;
  const merged = prs.filter((p) => p.status === 'merged').length;
  const finished = prs.filter((p) => !isOpenPr(p));
  const hint = finished.length
    ? `${finished.map((p) => `${prLabel(p.url)} ${TASK_PR_STATUSES[p.status].label.toLowerCase()}`).join(', ')}${finished.length < prs.length ? '; the open ones are below' : ''}`
    : 'None merged yet';
  return (
    <div
      className={cn('flex items-center gap-1.5 text-xs tabular-nums', merged === prs.length ? statusToneClasses('success').text : 'text-muted-foreground')}
      data-hint={hint}
      data-testid="task-card-pr-summary"
    >
      <GitPullRequest className="size-3.5 shrink-0" aria-hidden />
      {merged}/{prs.length} merged
    </div>
  );
}

/**
 * A card's pull requests (T-3): each one's state on GitHub now, and Merge for maintainers. With more than
 * one (SF-T-151), "2/3 merged" and a row for each one still open or a draft; the rest are in the hint.
 * SF-T-154: on a sub-task (`parent`), a PR its parent lists too is shared, merged from the parent.
 */
export function TaskCardPrs({
  taskId,
  prs,
  parent = null,
  hasSubtasks = false,
  waiting = [],
}: {
  taskId: string;
  prs: TaskPr[];
  parent?: TaskParent | null;
  /** SF-T-183: whose turn it is: Update is off while the AI builder already has the task. */
  waiting?: TaskWaiting[];
  /** SF-T-154: it has sub-tasks, so a change to one of them reads its PRs again (Merge waits for them). */
  hasSubtasks?: boolean;
}) {
  const mayMerge = usePermission('PRS_MERGE');
  const mayRun = usePermission('RUNS_START');
  const formatRef = useFormatTaskRef();
  const { live, reload } = useTaskPrsLive(taskId, prs, { followTask: hasSubtasks });
  const merging = usePrMerge(taskId, reload);
  const updating = usePrUpdate(taskId, reload);
  const aiHasIt = waiting.some((w) => w.target === 'ai');
  // SF-T-237: the Board's live runs (none outside a Board): Merge says so while one is going on the task.
  const runNote = mergeRunNote(useTaskActiveRun(taskId));

  if (!prs.length) return null;
  const rows = prs.length > 1 ? prs.filter(isOpenPr) : prs;

  return (
    <div className="space-y-1" data-no-row-click="" {...STOP_PROPS}>
      <TaskPrSummary prs={prs} />
      {rows.map((pr) => {
        const row = live?.prs.find((p) => p.url === pr.url);
        const rowLive = isOpenPr(pr) ? (row?.live ?? null) : null;
        const home = sharedPrParent({ parent }, pr.url);
        const update = prUpdateState(row?.migrations, { run: mayRun }, aiHasIt);
        return (
          <TaskPrRow
            key={pr.url}
            pr={pr}
            live={rowLive}
            checks={row?.checks ?? null}
            error={row?.error ?? (live && !live.github_connected ? "GitHub isn't connected to the factory" : null)}
            canMerge={mayMerge}
            onMerge={() => rowLive && merging.ask(pr, rowLive)}
            runNote={runNote}
            notReady={row?.subtasks_not_ready ?? null}
            sharedWith={home ? formatRef(home.number) : null}
            migrations={row?.migrations ?? null}
            onUpdate={update.shown ? () => void updating.update(pr.url) : undefined}
            updateEnabled={update.enabled}
            updateReason={update.reason}
            updating={updating.updating === pr.url}
          />
        );
      })}
      {merging.dialog}
    </div>
  );
}
